import { META_FIELD, warn, mapItems, assignComputed, DEFAULT_TYPE, deprecated } from 'datx-utils';
import { observable, set, toJS, intercept, isObservableArray, extendObservable, computed, action, reaction } from 'mobx';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

var PatchType;
(function (PatchType) {
    PatchType[PatchType["CRATE"] = 0] = "CRATE";
    PatchType[PatchType["UPDATE"] = 1] = "UPDATE";
    PatchType[PatchType["REMOVE"] = 2] = "REMOVE";
})(PatchType || (PatchType = {}));

var REGEX = /\$\{\s*([a-zA-Z0-9\-\_]+)\s*\}/g;
function msg(str, keys) {
    var msgStr = str;
    var match = REGEX.exec(msgStr);
    while (match) {
        msgStr = msgStr.replace(match[0], keys[match[1]]);
        match = REGEX.exec(msgStr);
    }
    return msgStr;
}
// tslint:disable-next-line:export-name
function error(message, keys) {
    if (keys === void 0) { keys = {}; }
    return new Error("[datx exception] " + msg(message, keys));
}

// tslint:disable:no-invalid-template-strings
var UNDEFINED_TYPE = 'The type needs to be defined if the object is not an instance of the model.';
var UNDEFINED_MODEL = 'No model is defined for the type ${type}.';
var NOT_A_CLONE = 'The given model is not a clone.';
var REF_NEEDS_COLLECTION = 'The model needs to be in a collection to be referenceable';
var REF_SINGLE = 'The reference ${key} can\'t be an array of values.';
var REF_ARRAY = 'The reference ${key} must be an array of values.';
var NO_REFS = 'You should save this value as a reference.';
var BACK_REF_READ_ONLY = 'Back references are read only';
var ID_READONLY = 'Model ID can\'t be updated directly. Use the `updateModelId` helper function instead.';
var TYPE_READONLY = 'Model type can\'t be changed after initialization.';
var MODEL_SINGLE_COLLECTION = 'A model can be in a single collection at once';
var ID_REQUIRED = 'Model id is required (autoincrement is disabled)';
var WRONG_REF_TYPE = 'The new reference type doesn\'t match to the declared one.';
var MODEL_REQUIRED = 'The model type is a required parameter. Do you maybe have a circular dependency?';
var SORTED_NO_WRITE = 'New models can\'t be added directly to a sorted view list';
var UNIQUE_MODEL = 'The models in this view need to be unique';
var VIEW_NAME_TAKEN = 'The name is already taken';
var DECORATE_MODEL = 'This mixin can only decorate models';

var FieldType;
(function (FieldType) {
    FieldType[FieldType["ID"] = 0] = "ID";
    FieldType[FieldType["TYPE"] = 1] = "TYPE";
    FieldType[FieldType["DATA"] = 2] = "DATA";
})(FieldType || (FieldType = {}));

var ReferenceType;
(function (ReferenceType) {
    ReferenceType[ReferenceType["TO_ONE"] = 0] = "TO_ONE";
    ReferenceType[ReferenceType["TO_MANY"] = 1] = "TO_MANY";
    ReferenceType[ReferenceType["TO_ONE_OR_MANY"] = 2] = "TO_ONE_OR_MANY";
})(ReferenceType || (ReferenceType = {}));

/**
 * Helper function used to iterate trough models prototype chain and collect data for all parents
 *
 * @export
 * @template T
 * @template U
 * @param {U} obj Given model
 * @param {(state: T, item: U) => T} reduceFn Function used to collect the data
 * @param {T} initialValue Initial reducer data
 * @returns {T} Collected data
 */
// tslint:disable-next-line:export-name
function reducePrototypeChain(obj, reduceFn, initialValue) {
    var value = initialValue;
    var model = obj;
    while (model) {
        value = reduceFn(value, model);
        model = Object.getPrototypeOf(model);
    }
    return value;
}

var DATX_KEY = Symbol.for('datx metadata');
function setMeta(model, value) {
    Object.defineProperty(model, DATX_KEY, {
        enumerable: false,
        value: value,
        writable: false,
    });
}
var DataStorage = /** @class */ (function () {
    function DataStorage() {
    }
    DataStorage.prototype.initModel = function (model) {
        var modelData = observable({ data: {}, meta: {} });
        setMeta(model, modelData);
        return modelData;
    };
    DataStorage.prototype.getModelData = function (model) {
        return this.__getModelData(model).data;
    };
    DataStorage.prototype.getModelDataKey = function (model, key) {
        var modelData = this.__getModelData(model);
        return modelData.data[key];
    };
    DataStorage.prototype.setModelData = function (model, data) {
        var modelData = this.__getModelData(model);
        set(modelData.data, data);
    };
    DataStorage.prototype.setModelDataKey = function (model, key, value) {
        var _a;
        this.setModelData(model, (_a = {}, _a[key] = value, _a));
    };
    DataStorage.prototype.getModelMeta = function (model) {
        var data = model[DATX_KEY];
        if (data) {
            return data.meta;
        }
        return this.setModelMeta(model, {});
    };
    DataStorage.prototype.getModelMetaKey = function (model, key) {
        return this.getModelMeta(model)[key];
    };
    DataStorage.prototype.setModelMeta = function (model, meta) {
        var modelData = this.__getModelData(model);
        set(modelData.meta, meta);
        return modelData.meta;
    };
    DataStorage.prototype.setModelMetaKey = function (model, key, value) {
        var _a;
        this.setModelMeta(model, (_a = {}, _a[key] = value, _a));
    };
    DataStorage.prototype.setModelClassMetaKey = function (model, key, value) {
        var _a;
        var data = model[DATX_KEY];
        if (!data) {
            data = {
                data: {},
                meta: {},
                references: {},
            };
            setMeta(model, data);
        }
        Object.assign(data.meta, (_a = {}, _a[key] = value, _a));
    };
    DataStorage.prototype.getModelClassMetaKey = function (obj, key) {
        return reducePrototypeChain(obj, function (value, model) {
            return value || (model[DATX_KEY] || { meta: {} }).meta[key] || null;
        }, null);
    };
    DataStorage.prototype.addModelDefaultField = function (model, key, value) {
        var _a, _b;
        var data = model.hasOwnProperty(DATX_KEY) && model[DATX_KEY];
        if (data) {
            Object.assign(data.data, (_a = {}, _a[key] = value, _a));
        }
        else {
            setMeta(model, {
                data: (_b = {}, _b[key] = value, _b),
                meta: {},
                references: {},
            });
        }
    };
    DataStorage.prototype.getModelDefaults = function (obj) {
        var defaults = reducePrototypeChain(obj, function (state, model) {
            return state.concat((model[DATX_KEY] || { data: [] }).data);
        }, []);
        return Object.assign.apply(Object, [{}].concat(defaults.reverse()));
    };
    DataStorage.prototype.addModelClassReference = function (model, key, options) {
        var _a, _b;
        if (!options.model && options.model !== 0) {
            throw error(MODEL_REQUIRED);
        }
        var data = model[DATX_KEY];
        if (data) {
            Object.assign(data.references, (_a = {}, _a[key] = options, _a));
        }
        else {
            setMeta(model, {
                data: {},
                meta: {},
                references: (_b = {}, _b[key] = options, _b),
            });
        }
    };
    DataStorage.prototype.getModelClassReferences = function (obj) {
        var defaults = reducePrototypeChain(obj, function (state, model) {
            return state.concat((model[DATX_KEY] || { references: {} }).references);
        }, []);
        return Object.assign.apply(Object, [{}].concat(defaults.reverse()));
    };
    DataStorage.prototype.getModelReferenceOptions = function (model, key) {
        var refs = this.getModelMetaKey(model, 'refs');
        return refs[key];
    };
    DataStorage.prototype.__getModelData = function (model) {
        return model[DATX_KEY] || this.initModel(model);
    };
    return DataStorage;
}());
var storage = new DataStorage();

/**
 * Get the type of the given model
 *
 * @export
 * @param {(IType|typeof PureModel|PureModel)} model Model to be checked
 * @returns {IType} Model type
 */
function getModelType(model) {
    if (typeof model === 'function') {
        // @ts-ignore
        return model.type;
    }
    else if (typeof model === 'object') {
        return getModelMetaKey(model, 'type') || model.constructor.type;
    }
    return model;
}
/**
 * Get the model identifier
 *
 * @export
 * @param {(PureModel|IIdentifier)} model Model to be checked
 * @returns {IIdentifier} Model identifier
 */
function getModelId(model) {
    if (model instanceof PureModel) {
        return getModelMetaKey(model, 'id');
    }
    return model;
}
/**
 * Get a collection the given model belongs to
 *
 * @export
 * @param {PureModel} model Model to be checked
 * @returns {PureCollection} A collection the given model belongs to
 */
function getModelCollection(model) {
    return getModelMetaKey(model, 'collection');
}
/**
 * Clone the given model
 *
 * @export
 * @template T
 * @param {T} model Model to be clones
 * @returns {T} Cloned model object
 */
function cloneModel(model) {
    var TypeModel = model.constructor;
    var rawData = modelToJSON(model);
    var meta = rawData[META_FIELD] || {};
    meta.originalId = meta.id;
    delete meta.id;
    var clone = new TypeModel(rawData);
    var collection = getModelCollection(model);
    if (collection) {
        collection.add(clone);
    }
    else {
        warn("The model is not in the collection. Referencing the original model won't be possible");
    }
    return clone;
}
/**
 * Get the original model for the cloned model
 *
 * @export
 * @param {PureModel} model Cloned model
 * @returns {PureModel} Original model
 */
function getOriginalModel(model) {
    var collection = getModelCollection(model);
    var originalId = getModelMetaKey(model, 'originalId');
    if (originalId) {
        if (!collection) {
            throw error(REF_NEEDS_COLLECTION);
        }
        return collection.findOne(model, originalId);
    }
    throw error(NOT_A_CLONE);
}
var READ_ONLY_META = ['fields', 'id', 'refs', 'type'];
/**
 * Bulk update the model data
 *
 * @export
 * @template T
 * @param {T} model Model to be updated
 * @param {IDictionary} data Data that should be assigned to the model
 * @returns {T}
 */
function updateModel(model, data) {
    var modelId = storage.getModelClassMetaKey(model.constructor, 'id') || 'id';
    var modelType = storage.getModelClassMetaKey(model.constructor, 'type');
    var rawData = data instanceof PureModel ? modelToJSON(data) : data;
    var keys = Object.keys(rawData);
    mergeMeta(model, storage.getModelMeta(model), data[META_FIELD]);
    startAction(model);
    keys.forEach(function (key) {
        if (key !== META_FIELD && key !== modelId && key !== modelType) {
            assignModel(model, key, rawData[key]);
        }
        else if (key === META_FIELD) {
            var metaKeys = Object.keys(rawData[key] || {});
            metaKeys.forEach(function (metaKey) {
                if (!READ_ONLY_META.includes(metaKey)) {
                    setModelMetaKey(model, metaKey, rawData[key][metaKey]);
                }
            });
        }
    });
    endAction(model);
    return model;
}
/**
 * Assign a property to a model
 *
 * @export
 * @template T
 * @param {T} model A model to modify
 * @param {string} key Property name
 * @param {*} value Property value
 */
function assignModel(model, key, value) {
    if (!(model instanceof PureModel)) {
        throw error('The given parameter is not a valid model');
    }
    var refs = getModelMetaKey(model, 'refs');
    if (!refs) {
        throw error('The given models is not initialized correctly');
    }
    startAction(model);
    if (key in refs) {
        assignModelRef(model, key, value);
    }
    else if (value instanceof PureModel) {
        endAction(model);
        throw error(NO_REFS, { key: key });
    }
    else {
        assignModelField(model, key, value);
    }
    endAction(model);
}
function assignModelField(model, key, value) {
    var fields = getModelMetaKey(model, 'fields');
    if (fields.indexOf(key) !== -1) {
        model[key] = value;
    }
    else {
        initModelField(model, key, value);
    }
}
function assignModelRef(model, key, value) {
    // const refs = getModelMetaKey(model, 'refs');
    model[key] = value;
}
function getMetaKeyFromRaw(data, key, model) {
    if (META_FIELD in data &&
        typeof data[META_FIELD] === 'object' &&
        data[META_FIELD] !== undefined) {
        return (data[META_FIELD] || {})[key];
    }
    if (model) {
        var modelId = storage.getModelClassMetaKey(model, key);
        return modelId && data[modelId];
    }
    return data && data[key];
}
/**
 * Get a serializable value of the model
 *
 * @export
 * @param {PureModel} model Model to serialize
 * @returns {IRawModel} Pure JS value of the model
 */
function modelToJSON(model) {
    var _a;
    var data = toJS(storage.getModelData(model));
    var rawMeta = Object.assign({}, storage.getModelMeta(model));
    delete rawMeta.collection;
    delete rawMeta.patch;
    var meta = toJS(rawMeta);
    var refs = {};
    Object.keys(meta.refs || {}).forEach(function (key) {
        refs[key] = { model: getModelType(meta.refs[key].model), type: meta.refs[key].type };
    });
    // console.log(meta.refs, Object.keys(meta.refs), refs)
    meta.refs = refs;
    delete meta.collection;
    var raw = Object.assign(data, (_a = {}, _a[META_FIELD] = meta, _a));
    var staticModel = model.constructor;
    var modelId = storage.getModelClassMetaKey(staticModel, 'id');
    var modelType = storage.getModelClassMetaKey(staticModel, 'type');
    if (meta && modelId) {
        raw[modelId] = meta.id;
    }
    if (meta && modelType) {
        raw[modelType] = meta.type;
    }
    return raw;
}
function getModelMetaKey(model, key) {
    return storage.getModelMetaKey(model, key);
}
function setModelMetaKey(model, key, value) {
    storage.setModelMetaKey(model, key, value);
    return;
}
function getModelClassRefs(type) {
    return storage.getModelClassReferences(type);
}

function isEmptyObject(data) {
    return Object.keys(data).length === 0;
}
function reverseAction(type) {
    switch (type) {
        case PatchType.CRATE:
            return PatchType.REMOVE;
        case PatchType.REMOVE:
            return PatchType.CRATE;
        default:
            return PatchType.UPDATE;
    }
}
function triggerAction(patchMeta, model) {
    var patch = __assign({}, patchMeta, { model: {
            id: getModelId(model),
            type: getModelType(model),
        } });
    var listeners = [];
    if ('__patchListeners' in model) {
        listeners.push.apply(listeners, model['__patchListeners'] || []);
    }
    var collection = getModelCollection(model);
    if (collection && '__patchListeners' in collection) {
        listeners.push.apply(listeners, collection['__patchListeners'] || []);
    }
    listeners.forEach(function (listener) {
        listener(patch);
    });
}
function startAction(model) {
    var patchData = storage.getModelMetaKey(model, 'patch') || { count: 0, oldValue: {}, newValue: {} };
    patchData.count++;
    storage.setModelMetaKey(model, 'patch', patchData);
}
function updateAction(model, key, value) {
    var patchData = storage.getModelMetaKey(model, 'patch') || { count: 0, oldValue: {}, newValue: {} };
    if (model[key] === value) {
        return;
    }
    if (!(key in patchData.oldValue)) {
        patchData.oldValue[key] = model[key];
    }
    patchData.newValue[key] = value;
    storage.setModelMetaKey(model, 'patch', patchData);
}
function endAction(model, patchType) {
    if (patchType === void 0) { patchType = PatchType.UPDATE; }
    var patchData = storage.getModelMetaKey(model, 'patch') || { count: 0, oldValue: {}, newValue: {} };
    patchData.count--;
    if (patchData.count === 0) {
        var newValue = toJS(patchData.newValue);
        var oldValue = toJS(patchData.oldValue);
        if (!isEmptyObject(newValue) || !isEmptyObject(oldValue)) {
            triggerAction({ newValue: newValue, oldValue: oldValue, patchType: patchType }, model);
        }
        storage.setModelMetaKey(model, 'patch', { count: 0, oldValue: {}, newValue: {} });
    }
    else {
        storage.setModelMetaKey(model, 'patch', patchData);
    }
}

function modelAddReference(model, key, newReference) {
    var refOptions = storage.getModelReferenceOptions(model, key);
    var newRefId = getModelId(newReference);
    var data = storage.getModelDataKey(model, key);
    if (refOptions.type === ReferenceType.TO_ONE) {
        storage.setModelDataKey(model, key, newRefId);
    }
    else if (refOptions.type === ReferenceType.TO_MANY || isObservableArray(data)) {
        data.push(newRefId);
    }
    else {
        storage.setModelDataKey(model, key, newReference);
    }
}
function modelRemoveReference(model, key, oldReference) {
    var refOptions = storage.getModelReferenceOptions(model, key);
    var oldRefId = getModelId(oldReference);
    var data = storage.getModelDataKey(model, key);
    if (refOptions.type === ReferenceType.TO_ONE) {
        storage.setModelDataKey(model, key, null);
    }
    else if (refOptions.type === ReferenceType.TO_MANY || isObservableArray(data)) {
        data.remove(oldRefId);
    }
    else {
        storage.setModelDataKey(model, key, null);
    }
}
function ensureModel(refOptions, collection) {
    return function (data) {
        var model = data;
        if (!(data instanceof PureModel) && typeof data === 'object') {
            if (!collection) {
                throw error(REF_NEEDS_COLLECTION);
            }
            model = collection.add(data, refOptions.model);
        }
        return getModelId(model);
    };
}
function partialRefUpdate(model, key, change) {
    var refOptions = storage.getModelReferenceOptions(model, key);
    var data = storage.getModelDataKey(model, key);
    var collection = getModelCollection(model);
    if (change.type === 'splice') {
        var added = change.added.map(ensureModel(refOptions, collection));
        data.splice.apply(data, [change.index, change.removedCount].concat(added));
        return null;
    }
    data[change.index] = ensureModel(refOptions, collection)(change.newValue);
    return null;
}
function backRefSplice(model, key, change, refOptions) {
    var property = refOptions.property;
    change.added.forEach(function (item) {
        modelAddReference(item, property, model);
    });
    var removed = model[key].slice(change.index, change.index + change.removedCount);
    removed.forEach(function (item) {
        modelRemoveReference(item, property, model);
    });
    return null;
}
function backRefChange(model, key, change, refOptions) {
    var property = refOptions.property;
    var oldValue = model[key].length > change.index ? model[key][change.index] : null;
    if (change.newValue) {
        modelAddReference(change.newValue, property, model);
    }
    if (oldValue) {
        modelRemoveReference(oldValue, property, model);
    }
    warn("This shouldn't have happened. Please open an issue: https://github.com/infinum/datx/issues/new");
    return null;
}
function partialBackRefUpdate(model, key, change) {
    var refOptions = storage.getModelReferenceOptions(model, key);
    if (change.type === 'splice') {
        return backRefSplice(model, key, change, refOptions);
    }
    return backRefChange(model, key, change, refOptions);
}
function getField(model, key) {
    return storage.getModelDataKey(model, key);
}
function updateField(model, key, value, type) {
    if (type === FieldType.TYPE) {
        throw error(TYPE_READONLY);
    }
    else if (type === FieldType.ID) {
        throw error(ID_READONLY);
    }
    startAction(model);
    var refs = getModelMetaKey(model, 'refs');
    if (key in refs) {
        updateRef(model, key, value);
    }
    else {
        updateAction(model, key, value);
        storage.setModelDataKey(model, key, value);
    }
    endAction(model);
}
function hasBackRef(item, property, target) {
    if (item[property] === null || item[property] === undefined) {
        return false;
    }
    else if (item[property] instanceof PureModel) {
        return item[property] === target;
    }
    else {
        return item[property].indexOf(target) !== -1;
    }
}
function getBackRef(model, key, refOptions) {
    var type = getModelType(refOptions.model);
    var collection = getModelCollection(model);
    if (!collection) {
        return null;
    }
    var backModels = collection
        .findAll(type)
        .filter(function (item) { return hasBackRef(item, refOptions.property, model); });
    var backData = observable.array(backModels, { deep: false });
    intercept(backData, function (change) { return partialBackRefUpdate(model, key, change); });
    return backData;
}
function getNormalRef(model, key, refOptions) {
    var value = storage.getModelDataKey(model, key);
    var collection = getModelCollection(model);
    if (!collection) {
        return null;
    }
    var dataModels = mapItems(value, function (id) { return id ? collection.findOne(refOptions.model, id) : id; });
    if (refOptions.type === ReferenceType.TO_MANY && !(dataModels instanceof Array)) {
        dataModels = [dataModels];
    }
    if (dataModels instanceof Array) {
        var data = observable.array(dataModels, { deep: false });
        intercept(data, function (change) { return partialRefUpdate(model, key, change); });
        return data;
    }
    return dataModels;
}
function getRef(model, key) {
    var refOptions = storage.getModelReferenceOptions(model, key);
    return (typeof refOptions.property === 'string')
        ? getBackRef(model, key, refOptions)
        : getNormalRef(model, key, refOptions);
}
function validateRef(refOptions, isArray, key) {
    if (refOptions.type === ReferenceType.TO_ONE && isArray) {
        throw error(REF_SINGLE, { key: key });
    }
    else if (refOptions.type === ReferenceType.TO_MANY && !isArray) {
        throw error(REF_ARRAY, { key: key });
    }
    else if (refOptions.property) {
        throw error(BACK_REF_READ_ONLY);
    }
}
function updateRef(model, key, value) {
    var refOptions = storage.getModelReferenceOptions(model, key);
    var check = refOptions.type === ReferenceType.TO_MANY ? value || [] : value;
    var isArray = check instanceof Array || isObservableArray(check);
    validateRef(refOptions, isArray, key);
    var collection = getModelCollection(model);
    startAction(model);
    var ids = mapItems(value, function (ref) {
        if (ref && collection) {
            if (ref instanceof PureModel) {
                var refType = getModelType(ref);
                if (refType !== getModelType(refOptions.model)) {
                    endAction(model);
                    throw error(WRONG_REF_TYPE);
                }
            }
            var instance = collection.findOne(refOptions.model, ref);
            if (!instance && typeof ref === 'object') {
                instance = collection.add(ref, refOptions.model);
            }
            endAction(model);
            return getModelId(instance || ref);
        }
        else if (ref instanceof PureModel) {
            endAction(model);
            throw error(REF_NEEDS_COLLECTION);
        }
        return ref;
    });
    if (refOptions.type === ReferenceType.TO_MANY) {
        ids = ids || [];
    }
    updateAction(model, key, ids);
    storage.setModelDataKey(model, key, ids);
    endAction(model);
}
function getModelRefsByType(model, type) {
    var refs = getModelMetaKey(model, 'refs');
    return Object.keys(refs)
        .filter(function (key) { return !refs[key].property; })
        .filter(function (key) { return getModelType(refs[key].model) === type; });
}
function updateModelReferences(model, newId, oldId, type) {
    var collection = getModelCollection(model);
    if (collection) {
        collection.getAllModels().map(function (item) {
            getModelRefsByType(item, type).forEach(function (ref) {
                var data = storage.getModelDataKey(item, ref);
                if (data instanceof Array || isObservableArray(data)) {
                    var targetIndex = data.indexOf(oldId);
                    if (targetIndex !== -1) {
                        data[targetIndex] = newId;
                    }
                }
                else if (data === oldId) {
                    storage.setModelDataKey(item, ref, newId);
                }
            });
        });
    }
}
/**
 * Updates the model identifier and all the existing references to the model
 *
 * @export
 * @param {PureModel} model Model to be updated
 * @param {IIdentifier} newId New model identifier
 */
function updateModelId(model, newId) {
    var collection = getModelCollection(model);
    var oldId = getModelId(model);
    var type = getModelType(model);
    setModelMetaKey(model, 'id', newId);
    var staticModel = model.constructor;
    var modelId = storage.getModelClassMetaKey(staticModel, 'id');
    if (modelId) {
        setRefId(model, modelId, newId);
    }
    if (collection) {
        // @ts-ignore - I'm bad and I should feel bad...
        collection.__changeModelId(oldId, newId, type);
    }
    updateModelReferences(model, newId, oldId, type);
}
/**
 * Get the id of the referenced model
 *
 * @export
 * @param {PureModel} model Source model
 * @param {string} key Referenced model property name
 * @returns {IIdentifier} Referenced model id
 */
function getRefId(model, key) {
    return storage.getModelDataKey(model, key);
}
/**
 * Set the id of the referenced model
 *
 * @export
 * @param {PureModel} model Source model
 * @param {string} key Referenced model property name
 * @param {IIdentifier} value The new value
 * @returns {void} Referenced model id
 */
function setRefId(model, key, value) {
    storage.setModelDataKey(model, key, value);
}

function initModelField(obj, key, defValue, type) {
    if (type === void 0) { type = FieldType.DATA; }
    var fields = getModelMetaKey(obj, 'fields');
    if (type === FieldType.ID && key in obj) {
        storage.setModelDataKey(obj, key, undefined);
    }
    // Initialize the observable field to the default value
    storage.setModelDataKey(obj, key, defValue);
    updateAction(obj, key, defValue);
    if (fields.indexOf(key) === -1) {
        fields.push(key);
    }
    assignComputed(obj, key, function () { return getField(obj, key); }, function (value) {
        updateField(obj, key, value, type);
    });
}
/**
 * Initialize a reference to other models
 *
 * @export
 * @param {PureModel} obj Model to which the reference should be added
 * @param {string} key Model property where the reference will be defined
 * @param {IReferenceOptions} options Reference options
 * @param {TRefValue} initialVal Initial reference value
 */
function initModelRef(obj, key, options, initialVal) {
    var refs = getModelMetaKey(obj, 'refs');
    // Initialize the observable field to the given value
    refs[key] = options;
    var isArray = options.type === ReferenceType.TO_MANY;
    storage.setModelDataKey(obj, key, isArray ? [] : undefined);
    updateAction(obj, key, isArray ? [] : undefined);
    assignComputed(obj, key, function () { return getRef(obj, key); }, function (value) {
        updateRef(obj, key, value);
    });
    if (!options.property && initialVal !== undefined) {
        obj[key] = initialVal;
    }
}
function prepareFields(data, meta, model) {
    var staticModel = model.constructor;
    var fields = meta.fields ? meta.fields.slice() : [];
    var classRefs = storage.getModelClassReferences(staticModel);
    var refs = Object.assign({}, classRefs, meta.refs);
    var defaults = storage.getModelDefaults(staticModel);
    Object.keys(data).concat(Object.keys(defaults))
        .forEach(function (key) {
        if (!(key in refs) && fields.indexOf(key) === -1) {
            fields.push(key);
        }
    });
    return { defaults: defaults, fields: fields, refs: refs };
}
function initModelData(model, data, meta, collection) {
    var _a = prepareFields(data, meta, model), defaults = _a.defaults, fields = _a.fields, refs = _a.refs;
    var staticModel = model.constructor;
    var modelId = storage.getModelClassMetaKey(staticModel, 'id');
    var modelType = storage.getModelClassMetaKey(staticModel, 'type');
    fields.forEach(function (key) {
        var type = FieldType.DATA;
        var value = data[key];
        if (value === undefined) {
            value = defaults[key];
        }
        if (key === (modelId || 'id')) {
            type = FieldType.ID;
            value = meta.id;
        }
        else if (key === modelType) {
            type = FieldType.TYPE;
            value = meta.type;
        }
        initModelField(model, key, value, type);
    });
    if (modelId && !(modelId in fields)) {
        initModelField(model, modelId, meta.id, FieldType.ID);
    }
    Object.keys(refs).forEach(function (key) {
        var opts = refs[key];
        var value = data[key] || defaults[key] || undefined;
        var models = collection ? collection.add(value, getModelType(opts.model)) : value;
        initModelRef(model, key, opts, models);
    });
}
function initModelMeta(model, data, collection) {
    var staticModel = model.constructor;
    var modelId = storage.getModelClassMetaKey(staticModel, 'id') || 'id';
    var modelType = storage.getModelClassMetaKey(staticModel, 'type');
    var dataMeta = META_FIELD in data && data[META_FIELD] || {};
    var type = (modelType && data[modelType]) || (dataMeta !== undefined && dataMeta.type) || getModelType(model);
    var id = (modelId && data[modelId]) || (dataMeta !== undefined && dataMeta.id);
    if (!id) {
        if (!staticModel.enableAutoId) {
            throw new Error(ID_REQUIRED);
        }
        id = staticModel.getAutoId();
        while (collection && collection.findOne(type, id)) {
            id = staticModel.getAutoId();
        }
    }
    var meta = {
        fields: [],
        id: id,
        refs: {},
        type: type,
    };
    var newMeta = META_FIELD in data && data[META_FIELD]
        ? mergeMeta(model, meta, data[META_FIELD])
        : storage.setModelMeta(model, meta);
    // tslint:disable-next-line:no-dynamic-delete
    delete data[META_FIELD];
    return Object.assign({}, newMeta);
}
function mergeMeta(model, meta, metaField) {
    var _a;
    if (metaField === void 0) { metaField = {}; }
    var toInit = { fields: [], refs: {} };
    toInit.fields = metaField.fields;
    (_a = meta.fields).push.apply(_a, (metaField.fields || []));
    toInit.refs = metaField.refs;
    Object.assign(meta.refs, metaField.refs || {});
    var exceptions = ['fields', 'refs', 'type', 'id'];
    Object.keys(metaField).forEach(function (field) {
        if (exceptions.indexOf(field) === -1) {
            meta[field] = metaField[field];
        }
    });
    var newMeta = storage.setModelMeta(model, meta);
    return Object.assign({}, newMeta, toInit);
}
function initModel(model, rawData, collection) {
    var staticModel = model.constructor;
    var data = Object.assign({}, staticModel.preprocess(rawData, collection));
    setModelMetaKey(model, 'collection', collection);
    var meta = initModelMeta(model, data, collection);
    initModelData(model, data, meta, collection);
}

// tslint:disable-next-line:no-unnecessary-class
var PureModel = /** @class */ (function () {
    function PureModel(rawData, collection) {
        if (rawData === void 0) { rawData = {}; }
        var staticClass = this.constructor;
        if (staticClass.__datxInitProps) {
            staticClass.__datxInitProps();
        }
        startAction(this);
        extendObservable(this, {});
        initModel(this, rawData, collection);
        endAction(this, PatchType.CRATE);
    }
    /**
     * Function used to preprocess the model input data. Called during the model initialization
     *
     * @static
     * @param {object} data Input data
     * @param {PureCollection} [collection] The collection the new model will belong to
     * @returns Target model data
     * @memberof Model
     */
    PureModel.preprocess = function (data, _collection) {
        return data;
    };
    /**
     * Method used for generating of automatic model ids
     *
     * @static
     * @returns {IIdentifier} A new model id
     * @memberof Model
     */
    PureModel.getAutoId = function () {
        return typeof this.autoIdValue === 'number' ? --this.autoIdValue : this.autoIdValue;
    };
    PureModel.toJSON = function () {
        return this.type;
    };
    /**
     * Model type used for serialization
     *
     * @static
     * @type {IType}
     * @memberof Model
     */
    PureModel.type = DEFAULT_TYPE;
    /**
     * Current autoincrement value used for automatic id generation
     *
     * @static
     * @type {IIdentifier}
     * @memberof Model
     */
    PureModel.autoIdValue = 0;
    PureModel.enableAutoId = true;
    return PureModel;
}());

function initCollectionModel(collection, data) {
    var type = getMetaKeyFromRaw(data, 'type');
    return upsertModel(data, type, collection);
}
function upsertModel(data, type, collection) {
    var _a;
    if (!type && type !== 0) {
        throw error(UNDEFINED_TYPE);
    }
    var staticCollection = collection.constructor;
    var TypeModel = staticCollection.types.find(function (item) { return item.type === type; });
    if (!TypeModel) {
        var DefaultModel = staticCollection.defaultModel;
        if (DefaultModel) {
            return new DefaultModel(__assign({}, data, (_a = {}, _a[META_FIELD] = __assign({}, data[META_FIELD] || {}, { type: type }), _a)), collection);
        }
        throw error(UNDEFINED_MODEL, { type: type });
    }
    var id = getMetaKeyFromRaw(data, 'id', TypeModel);
    var existingModel = id && collection.findOne(type, id);
    if (existingModel) {
        return updateModel(existingModel, data);
    }
    return new TypeModel(data, collection);
}
function isSelectorFunction(fn) {
    return (typeof fn === 'function') && (fn !== PureModel && !(fn.prototype instanceof PureModel));
}
function initModels(collection, data) {
    return data.map(function (item) { return initCollectionModel(collection, item); });
}

var View = /** @class */ (function () {
    function View(modelType, __collection, sortMethod, models, unique) {
        var _this = this;
        if (models === void 0) { models = []; }
        if (unique === void 0) { unique = false; }
        this.__collection = __collection;
        this.unique = unique;
        this.__models = observable.array([]);
        this.modelType = getModelType(modelType);
        var items = mapItems(models, function (item) {
            return _this.__getModel(item) || item;
        }).filter(Boolean);
        this.__models.replace(items);
        this.sortMethod = sortMethod;
        reaction(function () {
            var identifiers = _this.__models.filter(function (item) { return _this.__isIdentifier(item); });
            var check = identifiers.filter(function (model) { return _this.__collection.findOne(_this.modelType, model); });
            return check.length > 0;
        }, this.__reMap.bind(this));
    }
    Object.defineProperty(View.prototype, "length", {
        get: function () {
            return this.list.length;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(View.prototype, "list", {
        get: function () {
            var _this = this;
            // this.__reMap();
            var list = this.__models
                .filter(function (item) { return !_this.__isIdentifier(item); })
                .filter(Boolean);
            if (this.sortMethod) {
                var sortFn_1 = typeof this.sortMethod === 'string'
                    ? function (item) { return item[_this.sortMethod]; }
                    : this.sortMethod;
                list.sort(function (a, b) { return sortFn_1(a) - sortFn_1(b); });
            }
            var instances = observable.array(list, { deep: false });
            intercept(instances, this.__partialListUpdate.bind(this));
            return instances;
        },
        enumerable: true,
        configurable: true
    });
    View.prototype.toJSON = function () {
        return {
            modelType: this.modelType,
            models: this.__models.map(getModelId).slice(),
            unique: this.unique,
        };
    };
    Object.defineProperty(View.prototype, "snapshot", {
        get: function () {
            return this.toJSON();
        },
        enumerable: true,
        configurable: true
    });
    View.prototype.add = function (data) {
        var _this = this;
        var models = mapItems(data, function (item) { return _this.__collection.add(item, _this.modelType); });
        mapItems(models, function (instance) {
            if (!_this.unique || _this.__models.indexOf(instance) === -1) {
                _this.__models.push(instance);
            }
        });
        return models;
    };
    /**
     * Check if a model is in the collection
     *
     * @param {T|IIdentifier} model Model to check
     * @returns {boolean} The given model is in the collection
     * @memberof Collection
     */
    View.prototype.hasItem = function (model) {
        var id = getModelId(model);
        return Boolean(this.__models.find(function (item) { return getModelId(item) === id; }));
    };
    /**
     * Remove a model from the view
     *
     * @param {IIdentifier|T} model Model identifier
     * @memberof Collection
     */
    View.prototype.remove = function (model) {
        var item = this.__getModel(model);
        if (item) {
            this.__models.remove(item);
        }
    };
    View.prototype.removeAll = function () {
        this.__models.replace([]);
    };
    View.prototype.__getModel = function (model) {
        if (model instanceof PureModel) {
            return model;
        }
        return this.__collection.findOne(this.modelType, getModelId(model));
    };
    View.prototype.__isIdentifier = function (item) {
        return typeof item === 'string' || typeof item === 'number';
    };
    View.prototype.__reMap = function () {
        for (var i = 0; i < this.__models.length; i++) {
            if (this.__isIdentifier(this.__models[i])) {
                var model = this.__getModel(this.__models[i]);
                if (model) {
                    this.__models[i] = model;
                }
            }
        }
    };
    View.prototype.__partialListUpdate = function (change) {
        var _a;
        var _this = this;
        if (change.type === 'splice') {
            if (this.sortMethod && change.added.length > 0) {
                throw error(SORTED_NO_WRITE);
            }
            var added = change.added;
            var toRemove_1 = this.__models.slice(change.index, change.removedCount);
            if (this.unique) {
                added.forEach(function (newItem) {
                    if (_this.__models.indexOf(newItem) !== -1 && toRemove_1.indexOf(newItem) === -1) {
                        throw error(UNIQUE_MODEL);
                    }
                });
            }
            (_a = this.__models).splice.apply(_a, [change.index, change.removedCount].concat(added));
            return null;
        }
        if (this.sortMethod && change.newValue) {
            throw error(SORTED_NO_WRITE);
        }
        var newModel = this.__getModel(change.newValue);
        if (newModel) {
            var idIndex = this.__models.indexOf(newModel);
            if (this.unique && idIndex !== -1 && idIndex !== change.index) {
                throw error(UNIQUE_MODEL);
            }
            this.__models[change.index] = newModel;
        }
        return null;
    };
    __decorate([
        observable
    ], View.prototype, "sortMethod", void 0);
    __decorate([
        computed
    ], View.prototype, "length", null);
    __decorate([
        computed
    ], View.prototype, "list", null);
    __decorate([
        computed
    ], View.prototype, "snapshot", null);
    __decorate([
        action
    ], View.prototype, "add", null);
    __decorate([
        action
    ], View.prototype, "remove", null);
    __decorate([
        action
    ], View.prototype, "removeAll", null);
    __decorate([
        action
    ], View.prototype, "__reMap", null);
    __decorate([
        action
    ], View.prototype, "__partialListUpdate", null);
    return View;
}());

var PureCollection = /** @class */ (function () {
    function PureCollection(data) {
        var _this = this;
        if (data === void 0) { data = []; }
        this.__data = observable.array([], { deep: false });
        this.__views = [];
        this.__dataMap = {};
        this.__dataList = {};
        extendObservable(this, {});
        if (data instanceof Array) {
            this.insert(data);
        }
        else if (data && 'models' in data) {
            this.insert(data.models);
        }
        var staticCollection = this.constructor;
        var initViews = data && 'views' in data ? data.views : {};
        Object.keys(staticCollection.views).forEach(function (key) {
            var view = staticCollection.views[key];
            var init = initViews[key] || view;
            _this.addView(key, init.modelType, {
                mixins: view.mixins,
                models: init.models || [],
                sortMethod: view.sortMethod,
                unique: init.unique,
            });
        });
    }
    /**
     * Function for inserting raw models into the collection. Used when hydrating the collection
     *
     * @param {Array<IRawModel>} data Raw model data
     * @returns {Array<PureModel>} A list of initialized models
     * @memberof Collection
     */
    PureCollection.prototype.insert = function (data) {
        var models = initModels(this, data);
        this.__insertModel(models, undefined, undefined, true);
        return models;
    };
    PureCollection.prototype.add = function (data, model) {
        return data instanceof Array ? this.__addArray(data, model) : this.__addSingle(data, model);
    };
    PureCollection.prototype.findOne = function (model, id) {
        if (id instanceof PureModel) {
            return id;
        }
        return this.__findOneByType(model, id);
    };
    PureCollection.prototype.find = function (model, id) {
        if (id instanceof PureModel) {
            return id;
        }
        return isSelectorFunction(model)
            ? this.__data.find(model) || null
            : this.__findByType(model, id) || null;
    };
    /**
     * Filter models based on a matching function
     *
     * @param {TFilterFn} test Function used to match the models
     * @returns {(PureModel|null)} The matching models
     * @memberof Collection
     */
    PureCollection.prototype.filter = function (test) {
        return this.__data.filter(test);
    };
    /**
     * Find all matching models or all models if no type is given
     *
     * @param {(IType|typeof PureModel)} [model] Model type to select
     * @returns {Array<PureModel>} List of matching models
     * @memberof Collection
     */
    PureCollection.prototype.findAll = function (model) {
        var _a;
        if (model) {
            var type = getModelType(model);
            if (!(type in this.__dataList)) {
                set(this.__dataList, (_a = {}, _a[type] = observable.array([]), _a));
            }
            return this.__dataList[type];
        }
        return this.__data;
    };
    /**
     * Check if a model is in the collection
     *
     * @param {PureModel} model Model to check
     * @returns {boolean} The given model is in the collection
     * @memberof Collection
     */
    PureCollection.prototype.hasItem = function (model) {
        var id = getModelId(model);
        return Boolean(this.findOne(model, id));
    };
    PureCollection.prototype.removeOne = function (obj, id) {
        var model = null;
        if (typeof obj === 'object') {
            model = obj;
        }
        else if (id) {
            model = this.findOne(obj, id);
        }
        if (model) {
            this.__removeModel(model);
        }
    };
    PureCollection.prototype.remove = function (obj, id) {
        var model = typeof obj === 'object' ? obj : this.find(obj, id);
        if (model) {
            this.__removeModel(model);
        }
    };
    /**
     * Remove all models of the given model type from the collection
     *
     * @param {(IType|typeof PureModel)} type Model type
     * @memberof Collection
     */
    PureCollection.prototype.removeAll = function (type) {
        this.__removeModel(this.findAll(type).slice());
    };
    Object.defineProperty(PureCollection.prototype, "length", {
        /**
         * A total count of models in the collection
         *
         * @readonly
         * @type {number}
         * @memberof Collection
         */
        get: function () {
            return this.__data.length;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Get the serializable value of the collection
     *
     * @returns {IRawCollection} Pure JS value of the collection
     * @memberof Collection
     */
    PureCollection.prototype.toJSON = function () {
        var _this = this;
        var views = {};
        this.__views.forEach(function (key) {
            views[key] = _this[key].toJSON();
        });
        return {
            models: this.__data.map(modelToJSON),
            views: views,
        };
    };
    Object.defineProperty(PureCollection.prototype, "snapshot", {
        get: function () {
            return this.toJSON();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Reset the collection (remove all models)
     *
     * @memberof Collection
     */
    PureCollection.prototype.reset = function () {
        this.__data.forEach(function (model) {
            setModelMetaKey(model, 'collection', undefined);
            triggerAction({
                oldValue: modelToJSON(model),
                patchType: PatchType.REMOVE,
            }, model);
        });
        this.__data.replace([]);
        // tslint:disable-next-line:max-line-length
        this.__dataList = observable({}, {}, { deep: false });
        this.__dataMap = observable({}, {}, { deep: false });
    };
    PureCollection.prototype.getAllModels = function () {
        return this.__data.slice();
    };
    /**
     * Add a view to the collection
     *
     * @template T Model type of the view
     * @param {string} name View name
     * @param {(IModelConstructor<T>|IType)} type Model type the view will represent
     * @param {({
     *       sortMethod?: string|((item: T) => any),
     *       models?: Array<IIdentifier|PureModel>,
     *       unique?: boolean,
     *       mixins?: Array<(view: any) => any>,
     *     })} [{sortMethod, models, unique, mixins}={}] View options
     * @returns {View} The created view
     * @memberof PureCollection
     */
    PureCollection.prototype.addView = function (name, type, _a) {
        var _b = _a === void 0 ? {} : _a, sortMethod = _b.sortMethod, _c = _b.models, models = _c === void 0 ? [] : _c, unique = _b.unique, mixins = _b.mixins;
        if (name in this && this[name]) {
            throw error(VIEW_NAME_TAKEN);
        }
        var ViewConstructor = mixins
            ? mixins.reduce(function (view, mixin) {
                return mixin(view);
            }, View)
            : View;
        this.__views.push(name);
        this[name] = new ViewConstructor(type, this, sortMethod, models, unique);
        return this[name];
    };
    PureCollection.prototype.__addArray = function (data, model) {
        var _this = this;
        return data.filter(Boolean).map(function (item) { return _this.__addSingle(item, model); });
    };
    PureCollection.prototype.__addSingle = function (data, model) {
        if (!data || typeof data === 'number' || typeof data === 'string') {
            return data;
        }
        if (data instanceof PureModel) {
            if (!this.hasItem(data)) {
                this.__insertModel(data);
            }
            return data;
        }
        if (!model && model !== 0) {
            throw error(UNDEFINED_TYPE);
        }
        var type = getModelType(model);
        var modelInstance = upsertModel(data, type, this);
        this.__insertModel(modelInstance, type, undefined, true);
        return modelInstance;
    };
    PureCollection.prototype.__insertModel = function (model, type, id, insertOnly) {
        var _a;
        var _this = this;
        if (model instanceof Array) {
            model.forEach(function (item) {
                _this.__insertModel(item, type, id, insertOnly);
            });
            return;
        }
        var collection = getModelCollection(model);
        if (collection && collection !== this) {
            throw error(MODEL_SINGLE_COLLECTION);
        }
        var modelType = type || getModelType(model);
        var modelId = id || getModelId(model);
        var stringType = modelType.toString();
        var existingModel = this.findOne(modelType, modelId);
        if (existingModel) {
            if (!insertOnly) {
                updateModel(existingModel, model);
            }
            return;
        }
        this.__data.push(model);
        if (modelType in this.__dataList) {
            this.__dataList[modelType].push(model);
        }
        else {
            set(this.__dataList, stringType, observable.array([model], { deep: false }));
        }
        if (modelType in this.__dataMap) {
            set(this.__dataMap[modelType], modelId.toString(), model);
        }
        else {
            set(this.__dataMap, stringType, observable.object((_a = {}, _a[modelId] = model, _a), {}, { deep: false }));
        }
        setModelMetaKey(model, 'collection', this);
        triggerAction({
            newValue: modelToJSON(model),
            patchType: PatchType.CRATE,
        }, model);
    };
    PureCollection.prototype.__removeModel = function (model, type, id) {
        var _this = this;
        if (model instanceof Array) {
            model.forEach(function (item) {
                _this.__removeModel(item, type, id);
            });
            return;
        }
        var modelType = type || getModelType(model);
        var modelId = id || getModelId(model);
        triggerAction({
            oldValue: toJS(modelToJSON(model)),
            patchType: PatchType.REMOVE,
        }, model);
        this.__data.remove(model);
        this.__dataList[modelType].remove(model);
        set(this.__dataMap[modelType], modelId.toString(), undefined);
        this.__data.forEach(function (item) {
            var refs = getModelMetaKey(item, 'refs');
            var refKeys = Object.keys(refs || {});
            refKeys.forEach(function (key) {
                var refType = getModelType(refs[key].model);
                if (refType === modelType) {
                    var refIds = getRefId(item, key);
                    if (refIds instanceof Array) {
                        if (refIds.includes(modelId)) {
                            setRefId(item, key, refIds.filter(function (itemId) { return itemId !== modelId; }));
                        }
                    }
                    else if (refIds === modelId) {
                        setRefId(item, key, undefined);
                    }
                }
            });
        });
        setModelMetaKey(model, 'collection', undefined);
    };
    PureCollection.prototype.__findOneByType = function (model, id) {
        var _a;
        var type = getModelType(model);
        var stringType = type.toString();
        if (!(type in this.__dataMap)) {
            set(this.__dataMap, stringType, observable.object((_a = {}, _a[id] = null, _a), {}, { deep: false }));
        }
        else if (!(id in this.__dataMap[type])) {
            set(this.__dataMap[type], id.toString(), null);
        }
        return this.__dataMap[type][id] || null;
    };
    PureCollection.prototype.__findByType = function (model, id) {
        var _a;
        deprecated("\n      'find' method with the type/id combo and the 'remove' function are deprecated.\n      Please use 'findOne' and 'removeOne' instead.\n      They will always return the exact model or null if there is no match.\n    ");
        var type = getModelType(model);
        var stringType = type.toString();
        if (id) {
            if (!(type in this.__dataMap)) {
                set(this.__dataMap, stringType, observable.object((_a = {}, _a[id] = undefined, _a), {}, { deep: false }));
            }
            else if (!(id in this.__dataMap[type])) {
                set(this.__dataMap[type], id.toString(), undefined);
            }
            return this.__dataMap[type][id];
        }
        else {
            if (!(type in this.__dataList)) {
                set(this.__dataList, stringType, observable.array([], { deep: false }));
            }
            return this.__dataList[type].length ? this.__dataList[type][0] : null;
        }
    };
    // @ts-ignore - Used outside of the class, but marked as private to avoid undocumented use
    PureCollection.prototype.__changeModelId = function (oldId, newId, type) {
        this.__dataMap[type][newId] = this.__dataMap[type][oldId];
        // tslint:disable-next-line:no-dynamic-delete
        delete this.__dataMap[type][oldId];
    };
    /**
     * List of models available in the collection
     *
     * @static
     * @type {Array<typeof PureModel>}
     * @memberof Collection
     */
    PureCollection.types = [];
    PureCollection.views = {};
    PureCollection.defaultModel = PureModel;
    __decorate([
        observable.shallow
    ], PureCollection.prototype, "__dataMap", void 0);
    __decorate([
        observable.shallow
    ], PureCollection.prototype, "__dataList", void 0);
    __decorate([
        action
    ], PureCollection.prototype, "insert", null);
    __decorate([
        action
    ], PureCollection.prototype, "add", null);
    __decorate([
        action
    ], PureCollection.prototype, "removeOne", null);
    __decorate([
        action
    ], PureCollection.prototype, "remove", null);
    __decorate([
        action
    ], PureCollection.prototype, "removeAll", null);
    __decorate([
        computed
    ], PureCollection.prototype, "length", null);
    __decorate([
        action
    ], PureCollection.prototype, "reset", null);
    return PureCollection;
}());

/**
 * Check if a class is of a certain type
 *
 * @export
 * @param {Function} obj Class to check
 * @param {Function} type Type to check
 * @returns {boolean} Class is of the given type
 */
function isOfType(obj, type) {
    var model = obj;
    while (model) {
        if (model === type) {
            return true;
        }
        model = Object.getPrototypeOf(model);
    }
    return false;
}
function isModel(obj) {
    return isOfType(obj, PureModel);
}
function isCollection(obj) {
    return isOfType(obj, PureCollection);
}
function isView(obj) {
    return isOfType(obj, View);
}

function inversePatch(patch) {
    var patchType = reverseAction(patch.patchType);
    return {
        model: patch.model,
        newValue: patch.oldValue,
        oldValue: patch.newValue,
        patchType: patchType,
    };
}
function withPatches(Base) {
    if (isCollection(Base)) {
        var BaseClass = Base;
        var WithPatches = /** @class */ (function (_super) {
            __extends(WithPatches, _super);
            function WithPatches() {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var _this = _super.apply(this, args) || this;
                _this.__patchListeners = [];
                Object.defineProperty(_this, '__patchListeners', {
                    enumerable: false,
                });
                return _this;
            }
            WithPatches.prototype.applyPatch = function (patch) {
                var model = this.findOne(patch.model.type, patch.model.id);
                if (patch.patchType === PatchType.REMOVE) {
                    if (model) {
                        this.removeOne(model);
                    }
                }
                else if (patch.patchType === PatchType.UPDATE) {
                    if (model && patch.newValue) {
                        updateModel(model, patch.newValue);
                    }
                    else if (!model) {
                        throw error('The model should already exist for an update patch');
                    }
                    else {
                        throw error('New patch value isn\'t set for an update patch');
                    }
                }
                else {
                    if (model) {
                        throw error('The model shouldn\'t exist before a create patch');
                    }
                    else if (!patch.newValue) {
                        throw error('New patch value isn\'t set for a create patch');
                    }
                    else {
                        this.add(patch.newValue, patch.model.type);
                    }
                }
            };
            WithPatches.prototype.undoPatch = function (patch) {
                this.applyPatch(inversePatch(patch));
            };
            WithPatches.prototype.onPatch = function (listener) {
                var _this = this;
                this.__patchListeners.push(listener);
                return function () {
                    _this.__patchListeners = _this.__patchListeners.filter(function (item) { return item !== listener; });
                };
            };
            return WithPatches;
        }(BaseClass));
        return WithPatches;
    }
    else if (isModel(Base)) {
        var BaseClass = Base;
        // tslint:disable-next-line:max-classes-per-file
        var WithPatches = /** @class */ (function (_super) {
            __extends(WithPatches, _super);
            function WithPatches() {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var _this = _super.apply(this, args) || this;
                _this.__patchListeners = [];
                Object.defineProperty(_this, '__patchListeners', {
                    enumerable: false,
                });
                return _this;
            }
            WithPatches.prototype.applyPatch = function (patch) {
                if (patch.model.type === getModelType(this) && patch.model.id === getModelId(this)) {
                    updateModel(this, patch.newValue || {});
                }
            };
            WithPatches.prototype.undoPatch = function (patch) {
                this.applyPatch(inversePatch(patch));
            };
            WithPatches.prototype.onPatch = function (listener) {
                var _this = this;
                this.__patchListeners.push(listener);
                return function () {
                    _this.__patchListeners = _this.__patchListeners.filter(function (item) { return item !== listener; });
                };
            };
            return WithPatches;
        }(BaseClass));
        return WithPatches;
    }
    throw error('Only Models and Collections can be decorated with patches');
}

/**
 * Extends the model with some handy actions
 *
 * @export
 * @template T
 * @param {IModelConstructor<T>} Base Model to extend
 * @returns Extended model
 */
function withActions(Base) {
    var BaseClass = Base;
    if (!isModel(Base)) {
        throw error(DECORATE_MODEL);
    }
    var WithActions = /** @class */ (function (_super) {
        __extends(WithActions, _super);
        function WithActions() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        WithActions.prototype.update = function (data) {
            updateModel(this, data);
        };
        WithActions.prototype.clone = function () {
            // @ts-ignore
            return cloneModel(this);
        };
        WithActions.prototype.assign = function (key, value) {
            assignModel(this, key, value);
        };
        WithActions.prototype.addReference = function (key, value, options) {
            initModelRef(this, key, options, value);
        };
        WithActions.prototype.toJSON = function () {
            return modelToJSON(this);
        };
        return WithActions;
    }(BaseClass));
    return WithActions;
}

/**
 * Extends the model with the exposed meta data
 *
 * @export
 * @template T
 * @param {IModelConstructor<T>} Base Model to extend
 * @returns Extended model
 */
function withMeta(Base) {
    var BaseClass = Base;
    if (!isModel(BaseClass)) {
        throw error(DECORATE_MODEL);
    }
    var MetaClass = /** @class */ (function () {
        function MetaClass(instance) {
            this.__instance = instance;
        }
        Object.defineProperty(MetaClass.prototype, "collection", {
            get: function () {
                return getModelCollection(this.__instance);
            },
            enumerable: true,
            configurable: true
        });
        Object.defineProperty(MetaClass.prototype, "id", {
            get: function () {
                return getModelId(this.__instance);
            },
            enumerable: true,
            configurable: true
        });
        Object.defineProperty(MetaClass.prototype, "original", {
            get: function () {
                return getModelMetaKey(this.__instance, 'originalId') ? getOriginalModel(this.__instance) : undefined;
            },
            enumerable: true,
            configurable: true
        });
        Object.defineProperty(MetaClass.prototype, "refs", {
            get: function () {
                var _this = this;
                var refDefs = getModelMetaKey(this.__instance, 'refs') || {};
                var refs = {};
                Object.keys(refDefs).forEach(function (key) {
                    refs[key] = getRefId(_this.__instance, key);
                });
                return refs;
            },
            enumerable: true,
            configurable: true
        });
        Object.defineProperty(MetaClass.prototype, "snapshot", {
            get: function () {
                return modelToJSON(this.__instance);
            },
            enumerable: true,
            configurable: true
        });
        Object.defineProperty(MetaClass.prototype, "type", {
            get: function () {
                return getModelType(this.__instance);
            },
            enumerable: true,
            configurable: true
        });
        __decorate([
            computed
        ], MetaClass.prototype, "collection", null);
        __decorate([
            computed
        ], MetaClass.prototype, "id", null);
        __decorate([
            computed
        ], MetaClass.prototype, "original", null);
        __decorate([
            computed
        ], MetaClass.prototype, "refs", null);
        __decorate([
            computed
        ], MetaClass.prototype, "snapshot", null);
        __decorate([
            computed
        ], MetaClass.prototype, "type", null);
        return MetaClass;
    }());
    // tslint:disable-next-line:max-classes-per-file
    var WithMeta = /** @class */ (function (_super) {
        __extends(WithMeta, _super);
        function WithMeta() {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var _this = _super.apply(this, args) || this;
            // @ts-ignore
            _this.meta = new MetaClass(_this);
            Object.defineProperty(_this, 'meta', {
                enumerable: false,
            });
            return _this;
        }
        return WithMeta;
    }(BaseClass));
    return WithMeta;
}

var Model = /** @class */ (function (_super) {
    __extends(Model, _super);
    function Model() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Model.prototype.valueOf = function () {
        var raw = this.meta.snapshot;
        // tslint:disable-next-line:no-dynamic-delete
        delete raw[META_FIELD];
        return __assign({}, raw, { meta: {
                id: this.meta.id,
                type: this.meta.type,
            } });
    };
    Model.prototype.toString = function () {
        return JSON.stringify(this.valueOf(), null, 2);
    };
    return Model;
}(withPatches(withActions(withMeta(PureModel)))));

var Collection = /** @class */ (function (_super) {
    __extends(Collection, _super);
    function Collection() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Collection.defaultModel = Model;
    return Collection;
}(withPatches(PureCollection)));

var CompatModel = /** @class */ (function (_super) {
    __extends(CompatModel, _super);
    function CompatModel(initialData, collection) {
        var _this = _super.call(this, initialData, collection) || this;
        deprecated('CompatModel is just a migration tool. Please move to Model or PureModel as soon as possible.');
        Object.keys(_this.static.refs).forEach(function (prop) {
            var refs = getModelMetaKey(_this, 'refs');
            if (prop in refs) {
                return;
            }
            var ref = _this.static.refs[prop];
            var data = mapItems(_this[prop] || _this.static.defaults[prop], getModelId);
            assignComputed(_this, prop);
            if (typeof ref === 'object') {
                // Back reference
                initModelRef(_this, prop, {
                    model: ref.model,
                    property: ref.property,
                    type: ReferenceType.TO_ONE_OR_MANY,
                }, data);
            }
            else {
                // Normal reference
                initModelRef(_this, prop, {
                    model: ref,
                    type: ReferenceType.TO_ONE_OR_MANY,
                }, data);
            }
        });
        Object.keys(_this.static.defaults).forEach(function (prop) {
            if (!(prop in _this.static.refs) && !(prop in _this)) {
                initModelField(_this, prop, _this.static.defaults[prop]);
            }
        });
        return _this;
    }
    CompatModel.__datxInitProps = function () {
        if (this.idAttribute) {
            storage.setModelClassMetaKey(this, 'id', this.idAttribute);
        }
        if (this.typeAttribute) {
            storage.setModelClassMetaKey(this, 'type', this.typeAttribute);
        }
    };
    CompatModel.prototype.getRecordId = function () {
        deprecated('model.getRecordId is deprecated. Use getModelId() instead.');
        return getModelId(this);
    };
    CompatModel.prototype.getRecordType = function () {
        deprecated('model.getRecordType is deprecated. Use getModelType() instead.');
        return getModelType(this);
    };
    CompatModel.prototype.assign = function (key, value) {
        deprecated('model.assign is deprecated. Use assignModel() instead.');
        var type = FieldType.DATA;
        var modelId = storage.getModelClassMetaKey(this.static, 'id');
        var modelType = storage.getModelClassMetaKey(this.static, 'type');
        if (key === modelId) {
            type = FieldType.ID;
        }
        else if (key === modelType) {
            type = FieldType.TYPE;
        }
        updateField(this, key, value, type);
        return value;
    };
    CompatModel.prototype.assignRef = function (key, value, type) {
        deprecated('model.assignRef is deprecated. Use initModelRef() instead.');
        var refs = getModelMetaKey(this, 'refs');
        if (refs[key]) {
            return this[key] = value;
        }
        var model = type;
        if (!model) {
            if (value instanceof Array || isObservableArray(value)) {
                model = value.reduce(function (t, m) { return t || getModelType(m); }, null);
            }
            else if (value instanceof PureModel) {
                model = getModelType(value);
            }
        }
        if (!model) {
            throw new Error('The type property is missing');
        }
        initModelRef(this, key, {
            model: model,
            type: ReferenceType.TO_ONE_OR_MANY,
        }, value);
        return this[key];
    };
    CompatModel.prototype.update = function (data) {
        var _this = this;
        deprecated('model.update is deprecated. Use updateModel() instead.');
        var updateData = Object.assign({}, data);
        Object.keys(updateData).forEach(function (key) {
            if (typeof _this[key] === 'function') {
                // tslint:disable-next-line:no-dynamic-delete
                delete updateData[key];
            }
        });
        return updateModel(this, updateData);
    };
    Object.defineProperty(CompatModel.prototype, "static", {
        get: function () {
            deprecated('model.static is deprecated.');
            return this.constructor;
        },
        enumerable: true,
        configurable: true
    });
    CompatModel.prototype.toJS = function () {
        deprecated('model.toJS() is deprecated. Use modelToJSON() instead.');
        return modelToJSON(this);
    };
    Object.defineProperty(CompatModel.prototype, "snapshot", {
        get: function () {
            deprecated('model.snapshot is deprecated. Use modelToJSON() instead.');
            return modelToJSON(this);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CompatModel.prototype, "__collection", {
        // @ts-ignore
        // tslint:disable-next-line no-unused-variable
        get: function () {
            deprecated('model.__collection is deprecated. Use getModelCollection() instead.');
            return getModelCollection(this);
        },
        enumerable: true,
        configurable: true
    });
    CompatModel.refs = {};
    CompatModel.defaults = {};
    CompatModel.idAttribute = 'id';
    CompatModel.typeAttribute = '__type__';
    return CompatModel;
}(PureModel));

var CompatCollection = /** @class */ (function (_super) {
    __extends(CompatCollection, _super);
    function CompatCollection(data) {
        var _this = _super.call(this, data) || this;
        deprecated('CompatCollection is just a migration tool. Please move to Collection as soon as possible.');
        _this.static.types.forEach(function (model) {
            var type = getModelType(model);
            assignComputed(_this, type.toString(), function () { return _this.findAll(type); });
        });
        return _this;
    }
    Object.defineProperty(CompatCollection.prototype, "static", {
        get: function () {
            deprecated('collection.static is deprecated.');
            return this.constructor;
        },
        enumerable: true,
        configurable: true
    });
    CompatCollection.prototype.toJS = function () {
        deprecated('collection.toJS() is deprecated. Use collection.toJSON() instead');
        return this.toJSON();
    };
    CompatCollection.types = [CompatModel];
    return CompatCollection;
}(Collection));

function getClass(obj) {
    return (typeof obj === 'function' ? obj : obj.constructor);
}
function prepareDecorator(_obj, _key, opts) {
    if (opts && 'initializer' in opts) {
        opts['initializer'] = undefined;
        // Babel 7 + Decorators fix
        // Required to prevent the initializerDefineProperty call in babel-runtime
        // If initializer is undefined, the descriptor will be null and therefore
        // the initializerDefineProperty will be skipped
        // tslint:disable-next-line:max-line-length
        // https://github.com/babel/babel/blob/3aaafae053fa75febb3aa45d45b6f00646e30ba4/packages/babel-helpers/src/helpers.js#L1019
    }
}
/**
 * Set a model property as tracked
 *
 * @template T
 * @param {T} obj Target model
 * @param {string} key Property name
 * @returns {void}
 */
function propFn(obj, key, opts) {
    prepareDecorator(obj, key, opts);
    storage.addModelDefaultField(getClass(obj), key);
}
var prop = Object.assign(propFn, {
    /**
     * Set the default value for the model property
     *
     * @param {any} value The default property value
     * @returns {(obj: PureModel, key: string) => void}
     */
    defaultValue: function (value) {
        return function (obj, key, opts) {
            prepareDecorator(obj, key, opts);
            storage.addModelDefaultField(getClass(obj), key, value);
        };
    },
    /**
     * Add a reference to a single other model
     *
     * @param {typeof PureModel} refModel Model type the reference will point to
     * @returns {(obj: PureModel, key: string) => void}
     */
    toOne: function (refModel) {
        return function (obj, key, opts) {
            prepareDecorator(obj, key, opts);
            storage.addModelClassReference(getClass(obj), key, {
                model: refModel,
                type: ReferenceType.TO_ONE,
            });
        };
    },
    /**
     * Add a reference to multiple other models
     *
     * @param {typeof PureModel} refModel Model type the reference will point to
     * @param {string} [property] Use a foreign key from the other model to get this reference (computed back reference)
     * @returns {(obj: PureModel, key: string) => void}
     */
    toMany: function (refModel, property) {
        return function (obj, key, opts) {
            prepareDecorator(obj, key, opts);
            storage.addModelClassReference(getClass(obj), key, {
                model: refModel,
                property: property,
                type: ReferenceType.TO_MANY,
            });
        };
    },
    /**
     * Add a reference to a single or multiple other models
     *
     * @param {typeof PureModel} refModel Model type the reference will point to
     * @returns {(obj: PureModel, key: string) => void}
     */
    toOneOrMany: function (refModel) {
        return function (obj, key, opts) {
            prepareDecorator(obj, key, opts);
            storage.addModelClassReference(getClass(obj), key, {
                model: refModel,
                type: ReferenceType.TO_ONE_OR_MANY,
            });
        };
    },
    /**
     * Define the identifier property on the model
     *
     * @param {T} obj Target model
     * @param {string} key Identifier property name
     * @returns {void}
     */
    identifier: function (obj, key, opts) {
        prepareDecorator(obj, key, opts);
        storage.addModelDefaultField(getClass(obj), key);
        storage.setModelClassMetaKey(getClass(obj), 'id', key);
    },
    /**
     * Define the type property on the model
     *
     * @param {T} obj Target model
     * @param {string} key Type property name
     * @returns {void}
     */
    type: function (obj, key, opts) {
        prepareDecorator(obj, key, opts);
        storage.addModelDefaultField(getClass(obj), key);
        storage.setModelClassMetaKey(getClass(obj), 'type', key);
    },
});
function view(modelType, options) {
    if (options === void 0) { options = {}; }
    return function (obj, key, opts) {
        prepareDecorator(obj, key, opts);
        if (!Object.hasOwnProperty.call(obj.constructor, 'views')) {
            obj.constructor['views'] = {};
        }
        obj.constructor['views'][key] = __assign({ modelType: modelType }, options);
    };
}

function setupModel(Base, _a) {
    var _b = _a === void 0 ? { fields: {} } : _a, fields = _b.fields, references = _b.references, type = _b.type, idAttribute = _b.idAttribute, typeAttribute = _b.typeAttribute;
    var BaseClass = Base;
    if (!isModel(BaseClass)) {
        throw error(DECORATE_MODEL);
    }
    var ModelWithProps = /** @class */ (function (_super) {
        __extends(ModelWithProps, _super);
        function ModelWithProps() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        return ModelWithProps;
    }(BaseClass));
    if (type) {
        ModelWithProps.type = type;
    }
    if (idAttribute) {
        prop.identifier(ModelWithProps.prototype, idAttribute);
    }
    if (typeAttribute) {
        prop.type(ModelWithProps.prototype, typeAttribute);
    }
    if (fields) {
        Object.keys(fields).forEach(function (key) {
            prop.defaultValue(fields[key])(ModelWithProps.prototype, key);
        });
    }
    if (references) {
        Object.keys(references).forEach(function (key) {
            var _a = references[key], model = _a.model, property = _a.property;
            switch (references[key].type) {
                case ReferenceType.TO_ONE:
                    prop.toOne(model)(ModelWithProps.prototype, key);
                    return;
                case ReferenceType.TO_MANY:
                    prop.toMany(model, property)(ModelWithProps.prototype, key);
                    return;
                default:
                    prop.toOneOrMany(model)(ModelWithProps.prototype, key);
                    return;
            }
        });
    }
    return ModelWithProps;
}

export { Collection, CompatCollection, CompatModel, Model, PatchType, PureCollection, PureModel, ReferenceType, View, assignModel, cloneModel, getModelClassRefs, getModelCollection, getModelId, getModelMetaKey, getModelType, getOriginalModel, getRefId, initModelRef, isCollection, isModel, isView, modelToJSON, prop, setModelMetaKey, setRefId, setupModel, updateModel, updateModelId, view, withActions, withMeta, withPatches };
